import React, { useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { Auth } from '~/amplify';
import Button from '~/components/atom/Button';
import Divider from '~/components/atom/Divider';
import Icon from '~/components/atom/Icon';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import { Body, Heading3 } from '~/components/atom/Typography';
import OTPInput from '~/components/molecule/OTPInput';
import { Field, Form } from '~/components/organism/Forms';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import { reporter } from '~/hooks/useErrorReporter';
import { validateOtpCode } from '~/util/Validation/OTP';
import text from '../../text';

type Props = {
  onClose: () => void;
  onConfirm: () => Promise<void>;
  deleting: boolean;
  hasSmsEnabled: boolean;
};

const MFADeleteTotpModal: React.FC<Props> = ({
  onClose,
  onConfirm,
  deleting,
  hasSmsEnabled,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async ({ code }: { code: string }) => {
    setSubmitting(true);
    try {
      await Auth.verifyTOTPSetup({ code });
      await onConfirm();
      onClose();
      return;
    } catch (err) {
      reporter.captureMessage(
        `MFADeleteTotpModal: Failed to verify TOTP code before disabling MFA: ${err}`,
        'error',
      );
      return { [FORM_ERROR]: text.deleteTotpInvalidCode };
    } finally {
      setSubmitting(false);
    }
  };

  const validate = ({ code }: { code: string }) => {
    if (!validateOtpCode({ code })) {
      return { code: text.deleteTotpCodeLengthError };
    }
    return {};
  };

  const description = hasSmsEnabled
    ? text.confirmDeleteTotpWithSmsBody
    : text.confirmDeleteTotpWithoutSmsBody;

  const disabled = submitting || deleting;

  return (
    <Overlay onClose={onClose}>
      <Modal onClose={onClose} maxWidth="520px">
        <JustificationContainer
          direction="column"
          gap="l"
          padding={['xl', 'xl', 'l', 'xl']}
          width="100%"
        >
          <JustificationContainer gap="s" width="100%" align="center">
            <Icon name="exclamation" strokeWidth={2.3} />
            <Heading3 size="m" margin={[null]}>
              {text.confirmDeleteTitle}
            </Heading3>
          </JustificationContainer>

          <Body size="base" margin={[null]}>
            {description}
          </Body>

          <Divider margin={[null]} />

          <Form
            initialValues={{ code: '' }}
            onSubmit={onSubmit}
            validate={validate}
          >
            {({ handleSubmit, submitError: formError, invalid }) => (
              <form onSubmit={handleSubmit}>
                <JustificationContainer direction="column" gap="m" width="100%">
                  <Body size="base" margin={[null]}>
                    {text.deleteTotpOtpInstruction}
                  </Body>

                  {formError && (
                    <Body
                      margin={[null]}
                      color={{ group: 'danger' }}
                      size="base"
                    >
                      {formError}
                    </Body>
                  )}

                  <Field name="code">
                    {({ input }) => (
                      <OTPInput
                        length={6}
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        disabled={disabled}
                        autoFocus
                      />
                    )}
                  </Field>
                </JustificationContainer>

                <JustificationContainer
                  justification="end"
                  gap="base"
                  align="center"
                  margin={['l', null, null, null]}
                >
                  <TextButton
                    label={text.cancelButton}
                    onClick={onClose}
                    appearance="secondary"
                    size="medium"
                  />
                  <Button
                    appearance="danger"
                    size="medium"
                    type="submit"
                    disabled={invalid || disabled}
                    loading={deleting || submitting}
                    label={text.confirmDeleteButton}
                  />
                </JustificationContainer>
              </form>
            )}
          </Form>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

export default MFADeleteTotpModal;
